import React, { useRef } from 'react';
import { PostHogProvider } from 'posthog-js/react';
import { initAnalytics, getSharedEventProperties } from './core';
import type { AnalyticsConfig } from './types';

interface AnalyticsProviderProps {
  config: AnalyticsConfig;
  children: React.ReactNode;
}

/**
 * Wraps the app with PostHogProvider and initializes the analytics module.
 * Use this at the root of each app instead of raw PostHogProvider.
 *
 * initAnalytics runs synchronously during render (not in useEffect) so that
 * child mount effects can access shared properties (app, partner, platform).
 *
 * On PostHog load, registers `app`, `partner`, and `platform` as super-properties
 * so they appear on every event (including auto-captured `$pageview` events).
 * This is what lets the dashboard's HogQL queries identify per-app activity.
 */
export function AnalyticsProvider({ config, children }: AnalyticsProviderProps) {
  const initialized = useRef(false);

  // Initialize synchronously during render so _sharedProps is set before
  // any child useEffect fires (React runs child effects before parent effects).
  if (!initialized.current) {
    initAnalytics(config);
    initialized.current = true;
  }

  if (!config.posthogApiKey) {
    return <>{children}</>;
  }

  return (
    <PostHogProvider
      apiKey={config.posthogApiKey}
      options={{
        api_host: config.posthogHost,
        debug: config.debug ?? false,
        capture_pageview: false,
        loaded: (posthog) => {
          posthog.register(getSharedEventProperties());
        },
      }}
    >
      {children}
    </PostHogProvider>
  );
}
